import ApiService from './api';

/**
 * The action being reported to the NPS backend.
 * - `SHOWN`  — the survey was displayed to the user
 * - `SUBMIT` — the user submitted a score (and optionally feedback/topics)
 * - `LATER`  — the user deferred the survey (defers it for ~1 week)
 * - `IGNORE` — the user dismissed the survey outright
 */
export type NpsAction = 'SUBMIT' | 'LATER' | 'IGNORE' | 'SHOWN';

/** Outcome of the OMR review call-to-action, when it was shown. */
export type NpsOmrCtaAction = 'clicked' | 'skipped';

/** Answer from the eligibility endpoint. */
export interface NpsPromptEligibility {
  /** Whether the user should be prompted with the survey right now. */
  shouldShow: boolean;
  /** Optional human-readable explanation of why not, for debugging. */
  reason?: string;
  /** ISO timestamp of the next time the user becomes eligible. */
  nextEligibleAt?: string;
}

/** Request body for the NPS response endpoint. */
export interface NpsSubmitPayload {
  action: NpsAction;
  /** NPS score 0–10. Required when `action` is `SUBMIT`. */
  score?: number;
  /** Free-text feedback. */
  feedback?: string;
  /** Pre-defined topic chips the user selected. */
  topics?: string[];
  /** What triggered the action, e.g. `not_now`, `x`, `esc`. */
  trigger?: string;
  /** Outcome of the OMR review CTA, when it was shown. */
  omrCtaAction?: NpsOmrCtaAction;
}

/**
 * NPS (Net Promoter Score) Service
 * Handles NPS survey prompt eligibility and response submission.
 */
const NpsService = {
  /**
   * Check if the user should be shown an NPS prompt.
   * Never throws — a failure is treated as "not eligible", so a backend problem
   * can't block the user's flow.
   */
  async checkPromptEligibility(botId: string): Promise<NpsPromptEligibility> {
    try {
      const response = await ApiService.get(`/schaltzentrale/nps/prompt/${botId}`);
      return response.data;
    } catch (error) {
      console.error('NPS prompt eligibility check failed:', error);
      // Fail silently - don't block user flow
      return { shouldShow: false };
    }
  },

  /**
   * Submit an NPS response. Rethrows on failure so the caller can surface an
   * error state and let the user retry.
   */
  async submitResponse(payload: NpsSubmitPayload): Promise<any> {
    try {
      const response = await ApiService.post('/schaltzentrale/nps/response', payload);
      return response.data;
    } catch (error) {
      console.error('NPS response submission failed:', error);
      throw error;
    }
  },
};

export default NpsService;
export { NpsService };
